import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, Eye, EyeOff } from "lucide-react";

export default function DoctorLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const doLogin = async (em, pw) => {
        setLoading(true);
        try {
            const u = await login(em, pw);
            if (u.role !== "doctor") {
                toast.error("This account is not a doctor account.");
                return;
            }
            toast.success(`Welcome, ${u.name}`);
            navigate("/doctor");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-screen" data-testid="doctor-login-page">
            <Navbar />
            <main className="pt-28 pb-20">
                <section className="max-w-md mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">DOCTOR PORTAL</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-8">
                        Login as a <span className="brand-gradient-text">doctor</span>
                    </h1>
                    <div className="card-soft p-7">
                        <form onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }} className="space-y-4" data-testid="doctor-login-form">
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">EMAIL</label>
                                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    data-testid="doctor-login-email" />
                            </div>
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">PASSWORD</label>
                                <div className="relative mt-1">
                                    <input required type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="doctor-login-password" />
                                    <button type="button" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-3 grid place-items-center text-gray-400">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="doctor-login-submit">
                                <Stethoscope className="w-4 h-4" /> {loading ? "Signing in…" : "Sign in"}
                            </button>
                        </form>
                        <div className="mt-5 text-center text-sm text-gray-500">
                            Not registered yet? <Link to="/doctor/signup" className="text-[var(--medx-red)] font-semibold hover:underline">Create doctor account</Link>
                        </div>
                        <div className="text-center mt-2">
                            <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[var(--medx-red)]" data-testid="doctor-forgot-password-link">
                                Forgot your password?
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
